import CustomField from "components/Common/CustomField"
import CustomLabel from "components/Common/CustomLabel"
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik"
import React, { useEffect, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  Row,
} from "reactstrap"
import { Trash } from "lucide-react"
import * as Yup from "yup"
import { showAlert } from "store/reducers/alertReducer"
import IsLoading from "components/Shared/IsLoading"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import {
  arabicNameOrNumberPattern,
  englishNameOrNumberPattern,
  formatDateForAPI,
  getLocalizedName,
} from "components/Common/Utils"
import { hoursOptions } from "components/Common/constants"
import { getUserLists } from "store/actions/userAction"
import {
  createPracticalTraining,
  getPracticalTrainingDetails,
  updatePracticalTraining,
} from "store/actions/practicalTrainingAction"
import { getJobTitlesList } from "store/actions/commonAction"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import MatchedCandidates from "./components/MatchedCandidates"
import CustomMultiSelect from "components/Shared/CustomMultiSelect"
import CustomSingleSelect from "components/Shared/CustomSingleSelect"
import moment from "moment"

const initialValues = {
  name: "",
  name_ar: "",
  site: "",
  station: "",
  start_date: "",
  end_date: "",
  start_time: "",
  end_time: "",
  duration: null,
  trainer_id: null,
  trainees_each_group: null,
  training_jobs: [
    {
      job_title_id: null,
      no_of_job: null,
      is_saudi: null,
      employee_experience: 0, // 0:all, 1: Experienced, 2: New
    },
  ],
  facilitator_ids: [],
  trainer_assistant_id: null,
}

const CreatePracticalTraining = ({ mode }) => {
  const [modal1, setModal1] = useState(false)
  const toggle1 = () => setModal1(!modal1)
  const [pasteDateInEdit, setPasteDateInEdit] = useState(null)

  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const id = queryParams.get("id") // Extracting 'id'

  const nationalities = [
    { label: t("All"), value: null },
    { label: t("Saudi"), value: 1 },
    { label: t("Non Saudi"), value: 0 },
  ]

  const experiences = [
    { label: t("All"), value: 0 },
    { label: t("Experienced"), value: 1 },
    { label: t("New"), value: 2 },
  ]

  const { isLoading, isGettingDetails, practicalTrainingDetails, totalCount1 } =
    useSelector(state => state.PracticalTraining)
  const { usersList } = useSelector(state => state.Users)
  const { jobTitlesList } = useSelector(state => state.Common)

  useEffect(() => {
    if (mode === "update" || (mode === "add" && id)) {
      getDetails()
    }
  }, [mode, id])

  const getDetails = () => {
    dispatch(getPracticalTrainingDetails(id))
  }

  useEffect(() => {
    const getAllJobTitles = () => {
      dispatch(getJobTitlesList())
    }
    getAllJobTitles()
  }, [])

  const trainers = useMemo(() => {
    if (!usersList) return []

    const roles = ["trainer", "trainer +", "trainer+"]

    return usersList
      .filter(item =>
        roles.some(role => item.role.toLowerCase().includes(role))
      )
      .map(user => {
        return {
          label: user.full_name,
          value: user.id,
        }
      })
  }, [usersList])

  const facilitators = useMemo(() => {
    if (!usersList) return []

    const roles = ["attendance admin"]
    const filteredUsers = usersList.filter(item =>
      roles.some(role => item.role.toLowerCase().includes(role.toLowerCase()))
    )

    return filteredUsers.map(user => {
      return {
        label: user.full_name,
        value: user.id,
      }
    })
  }, [usersList])

  useEffect(() => {
    if (
      !isGettingDetails &&
      practicalTrainingDetails &&
      (mode === "update" || (mode === "add" && id))
    ) {
      const {
        name,
        name_ar,
        site,
        start_date,
        start_time,
        end_date,
        end_time,
        station,
        duration,
        trainer_id,
        trainees_each_group,
        facilitator_ids,
        training_jobs,
        trainer_assistant_id,
      } = practicalTrainingDetails || {}

      const updatedTrainingJobs = training_jobs
        ? training_jobs?.map(item => {
            return { ...item, job_title_id: Number(item.job_title_id) }
          })
        : []

      setPasteDateInEdit(start_date)

      setFormValues(prev => ({
        name,
        name_ar,
        site,
        start_date: mode === "add" ? null : start_date,
        start_time: mode === "add" ? null : start_time,
        end_date: mode === "add" ? null : end_date,
        end_time: mode === "add" ? null : end_time,
        station,
        duration,
        trainer_id,
        trainees_each_group,
        trainer_assistant_id,
        training_jobs: updatedTrainingJobs,
        facilitator_ids: usersList
          .filter(item => facilitator_ids?.includes(item.id))
          .map(item => ({ label: item.full_name, value: item.id })),
      }))
    }
  }, [mode, practicalTrainingDetails, usersList])

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(t("Field is required"))
      .matches(
        englishNameOrNumberPattern,
        t("Only English letters are allowed")
      ),
    name_ar: Yup.string()
      .matches(arabicNameOrNumberPattern, t("Only Arabic letters are allowed"))
      .required(t("Field is required")),
    site: Yup.string().required(t("Field is required")),
    station: Yup.string().required(t("Field is required")),
    start_date: Yup.date().required(t("Field is required")),
    start_time: Yup.string().required(t("Field is required")),
    end_time: Yup.string()
      .required(t("Field is required"))
      .test(
        "is-after-start",
        t("End time cannot be before start time"),
        function (endValue) {
          const { start_time, start_date, end_date } = this.parent

          if (!start_date || !end_date || !start_time || !endValue) return true // Skip if any is missing

          const dateTimeFormat = "YYYY-MM-DD HH:mm"

          const startDateTime = moment(
            `${moment(start_date).format("YYYY-MM-DD")} ${start_time}`,
            dateTimeFormat
          )

          const endDateTime = moment(
            `${moment(end_date).format("YYYY-MM-DD")} ${endValue}`,
            dateTimeFormat
          )

          return endDateTime.isAfter(startDateTime)
        }
      ),
    end_date: Yup.date()
      .required(t("Field is required"))
      .min(Yup.ref("start_date"), t("End date cannot be before start date")),
    duration: Yup.string().required(t("Field is required")),
    trainer_id: Yup.string().required(t("Field is required")),
    training_jobs: Yup.array()
      .of(
        Yup.object().shape({
          job_title_id: Yup.string().required(t("Job Title is required")),
          no_of_job: Yup.number()
            .required(t("Number of Job is required"))
            .positive(t("Must be a positive number"))
            .integer(t("Must be an integer")),
        })
      )
      .min(1, t("At least one job is required")),
    trainees_each_group: Yup.number()
      .required(t("Field is required"))
      .positive()
      .integer(),
    facilitator_ids: Yup.array().min(
      1,
      t("At least one facilitator is required")
    ),
    trainer_assistant_id: Yup.string().required(t("Field is required")),
  })

  const [formValues, setFormValues] = useState(initialValues)

  // GET USER LISTS
  useEffect(() => {
    const fetchUserLists = () => {
      dispatch(getUserLists())
    }
    fetchUserLists()
  }, [])

  const handleOnSubmit = values => {
    const {
      name,
      name_ar,
      site,
      station,
      start_date,
      start_time,
      duration,
      trainer_id,
      end_date,
      end_time,
      trainees_each_group,
      training_jobs,
      facilitator_ids,
      trainer_assistant_id,
    } = values

    const payload = {
      name,
      name_ar,
      duration,
      site,
      station,
      start_date: formatDateForAPI(start_date),
      end_date: formatDateForAPI(end_date),
      start_time,
      trainer_id,
      end_time,
      trainees_each_group,
      training_jobs,
      facilitator_ids: facilitator_ids.map(item => item.value),
      trainer_assistant_id,
    }

    if (mode === "add") {
      dispatch(createPracticalTraining(payload))
        .then(action => {
          const { statusCode, success, message } = action?.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "update") {
      dispatch(updatePracticalTraining({ selectedUuid: id, payload }))
        .then(action => {
          const { statusCode, success, message } = action?.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  const showName = (id, type) => {
    if (type !== "is_saudi" && type !== "employee_experience" && !id)
      return null
    if (type === "trainer_id") {
      const found = usersList.find(item => item.id === id)
      return {
        label: found?.full_name,
        value: found?.id,
      }
    } else if (type === "duration") {
      return hoursOptions.find(item => item.value === id)
    } else if (type === "job_title_id") {
      const found = jobTitlesList.find(item => item.id === id)
      return {
        label: getLocalizedName(found, language),
        value: found?.id,
      }
    } else if (type === "is_saudi") {
      const found = nationalities.find(item => item.value === id)
      return {
        label: found?.label,
        value: found?.value,
      }
    } else if (type === "employee_experience") {
      const found = experiences.find(item => item.value === id)
      return {
        label: found?.label,
        value: found?.value,
      }
    }
  }

  if ((mode === "update" || (mode === "add" && id)) && isGettingDetails) {
    return <IsLoading />
  }

  return (
    <div className="page-content">
      <Container fluid>
        <h4 className="card-title mb-4">
          <BackButton />
          {mode === "add"
            ? t("Add Practical Training")
            : t("Update Practical Training")}
        </h4>

        <Card>
          <CardBody>
            <Formik
              initialValues={formValues}
              enableReinitialize={true}
              validationSchema={validationSchema}
              onSubmit={handleOnSubmit}
            >
              {({ values, errors, touched, setFieldValue, isValid, dirty }) => {
                return (
                  <Form>
                    <Row className="theoretical_form_row">
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="name"
                            label={t("Name (English)")}
                            isRequired={true}
                          />
                          <CustomField
                            name="name"
                            type="text"
                            placeholder={t("Name (English)")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="name_ar"
                            label={t("Name (Arabic)")}
                            isRequired={true}
                          />
                          <CustomField
                            name="name_ar"
                            type="text"
                            placeholder={t("Name (Arabic)")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="station"
                            label={t("Station")}
                            isRequired={true}
                          />
                          <CustomField
                            name="station"
                            type="text"
                            placeholder={t("Station")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="site"
                            label={t("Site")}
                            isRequired={true}
                          />
                          <CustomField
                            name="site"
                            type="text"
                            placeholder={t("Site")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>

                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="start_date"
                            label={t("Start Date")}
                            isRequired={true}
                          />
                          <Flatpickr
                            options={{
                              dateFormat: "d-m-Y",
                              minDate: pasteDateInEdit
                                ? new Date(pasteDateInEdit)
                                : "today",
                            }} // Format: dd-mm-yyyy
                            value={
                              values.start_date
                                ? new Date(values.start_date)
                                : null
                            }
                            onChange={selectedDates =>
                              setFieldValue("start_date", selectedDates[0])
                            }
                            placeholder={t("Select a date")}
                            className="form-control"
                          />
                          <ErrorMessage
                            name={"start_date"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>

                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="end_date"
                            label={t("End Date")}
                            isRequired={true}
                          />
                          <Flatpickr
                            options={{
                              dateFormat: "d-m-Y",
                              minDate: new Date(values.start_date),
                            }} // Format: dd-mm-yyyy
                            value={
                              values.end_date ? new Date(values.end_date) : null
                            } // Ensure end_date is a Date object
                            onChange={selectedDates =>
                              setFieldValue("end_date", selectedDates[0])
                            }
                            placeholder={t("Select a date")}
                            className="form-control"
                            disabled={!values.start_date}
                          />
                          <ErrorMessage
                            name={"end_date"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="start_time"
                            label={t("Start Time")}
                            isRequired={true}
                          />
                          <CustomField
                            name="start_time"
                            type="time"
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>

                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="end_time"
                            label={t("End Time")}
                            isRequired={true}
                          />
                          <CustomField
                            name="end_time"
                            type="time"
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="duration"
                            label={t("Duration")}
                            isRequired={true}
                          />
                          <CustomSingleSelect
                            name="duration"
                            value={showName(values.duration, "duration")}
                            options={hoursOptions}
                            placeholder={t("Select Duration")}
                            onChange={e => setFieldValue("duration", e?.value)}
                          />

                          <ErrorMessage
                            name={"duration"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="trainees_each_group"
                            label={t("Total Per Group")}
                            isRequired={true}
                          />
                          <CustomField
                            name="trainees_each_group"
                            type="number"
                            placeholder={t("Total Per Group")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="trainer_id"
                            label={t("Select Trainer")}
                            isRequired={true}
                          />
                          <CustomSingleSelect
                            name="trainer_id"
                            value={trainers.find(
                              item => item.value === values.trainer_id
                            )}
                            options={trainers}
                            label={t("Select Trainer")}
                            placeholder={t("Select Trainer")}
                            onChange={selectedOption =>
                              setFieldValue("trainer_id", selectedOption?.value)
                            }
                            isInvalid={
                              touched?.trainer_id && errors?.trainer_id
                            }
                          />
                          <ErrorMessage
                            name={"trainer_id"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="trainer_assistant_id"
                            label={t("Select Facilitator")}
                            isRequired={true}
                          />
                          <CustomSingleSelect
                            name="trainer_assistant_id"
                            value={trainers.find(
                              item => item.value === values.trainer_assistant_id
                            )}
                            options={trainers}
                            label={t("Select Facilitator")}
                            placeholder={t("Select Facilitator")}
                            onChange={selectedOption =>
                              setFieldValue(
                                "trainer_assistant_id",
                                selectedOption?.value
                              )
                            }
                            isInvalid={
                              touched?.trainer_assistant_id &&
                              errors?.trainer_assistant_id
                            }
                          />
                          <ErrorMessage
                            name={"trainer_assistant_id"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>
                      <Col lg={3} md={4} xs={12}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="facilitator_ids"
                            label={t("Select Attendance Admin")}
                            isRequired={true}
                          />
                          <CustomMultiSelect
                            name="facilitator_ids"
                            value={values.facilitator_ids}
                            onChange={e => {
                              setFieldValue("facilitator_ids", e)
                            }}
                            options={facilitators}
                            placeholder={` ${t("Select Attendance Admin")}`}
                          />
                          <ErrorMessage
                            name={"facilitator_ids"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>
                      <FieldArray name="training_jobs">
                        {({ push, remove }) => (
                          <>
                            {values?.training_jobs?.map((job, index) => (
                              <Row key={index} className="job-group mx-0">
                                {values.training_jobs?.length > 1 &&
                                  (mode !== "update" ||
                                    (mode === "update" &&
                                      index + 1 >
                                        practicalTrainingDetails?.training_jobs
                                          ?.length)) && (
                                    <div className="g_end">
                                      <button
                                        style={{
                                          position: "absolute",
                                          left:
                                            language === "sa" ? 10 : "unset",
                                          right:
                                            language === "sa" ? "unset" : 10,
                                          top: 0,
                                        }}
                                        className="bg-danger sm_delete_btn g_center"
                                        onClick={() => remove(index)}
                                      >
                                        <Trash size={12} />
                                      </button>
                                    </div>
                                  )}

                                <Col lg={3} md={4} xs={12}>
                                  <div className="form_field">
                                    <CustomLabel
                                      htmlFor={`training_jobs.${index}.job_title_id`}
                                      label={t("Job Title")}
                                      isRequired
                                    />

                                    <CustomSingleSelect
                                      name={`training_jobs.${index}.job_title_id`}
                                      value={showName(
                                        values.training_jobs[index]
                                          .job_title_id,
                                        "job_title_id"
                                      )}
                                      options={jobTitlesList.map(item => {
                                        return {
                                          label: getLocalizedName(
                                            item,
                                            language
                                          ),
                                          value: item.id,
                                        }
                                      })}
                                      placeholder={t("Select Job Title")}
                                      onChange={e =>
                                        setFieldValue(
                                          `training_jobs.${index}.job_title_id`,
                                          e?.value
                                        )
                                      }
                                      isDisabled={
                                        mode === "update" &&
                                        index <
                                          practicalTrainingDetails
                                            ?.training_jobs?.length
                                      }
                                    />

                                    <ErrorMessage
                                      name={`training_jobs.${index}.job_title_id`}
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </Col>
                                <Col lg={3} md={4} xs={12}>
                                  <div className="form_field">
                                    <CustomLabel
                                      htmlFor={`training_jobs.${index}.no_of_job`}
                                      label={t("Number Of Jobs")}
                                      isRequired
                                    />

                                    <Field
                                      name={`training_jobs.${index}.no_of_job`}
                                      type="number"
                                      className="form-control"
                                      placeholder={t("Number Of Jobs")}
                                    />
                                    <ErrorMessage
                                      name={`training_jobs.${index}.no_of_job`}
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </Col>
                                <Col lg={3} md={4} xs={12}>
                                  <div className="form_field">
                                    <CustomLabel
                                      htmlFor={`training_jobs.${index}.is_saudi`}
                                      label={t("Nationality")}
                                    />
                                    <CustomSingleSelect
                                      name={`training_jobs.${index}.is_saudi`}
                                      value={showName(
                                        values.training_jobs[index].is_saudi,
                                        "is_saudi"
                                      )}
                                      options={nationalities}
                                      placeholder={t("Select Job Title")}
                                      onChange={e =>
                                        setFieldValue(
                                          `training_jobs.${index}.is_saudi`,
                                          e?.value
                                        )
                                      }
                                      isDisabled={
                                        mode === "update" &&
                                        index <
                                          practicalTrainingDetails
                                            ?.training_jobs?.length
                                      }
                                    />
                                  </div>
                                </Col>
                                <Col lg={3} md={4} xs={12}>
                                  <div className="form_field">
                                    <CustomLabel
                                      htmlFor={`training_jobs.${index}.employee_experience`}
                                      label={t("Employee Experience")}
                                    />

                                    <CustomSingleSelect
                                      name={`training_jobs.${index}.employee_experience`}
                                      value={showName(
                                        values.training_jobs[index]
                                          .employee_experience,
                                        "employee_experience"
                                      )}
                                      options={experiences}
                                      placeholder={t(
                                        "Select Employee Experience"
                                      )}
                                      onChange={e =>
                                        setFieldValue(
                                          `training_jobs.${index}.employee_experience`,
                                          e?.value
                                        )
                                      }
                                      isDisabled={
                                        mode === "update" &&
                                        index <
                                          practicalTrainingDetails
                                            ?.training_jobs?.length
                                      }
                                    />
                                  </div>
                                </Col>
                              </Row>
                            ))}
                            <Col lg={12} className="my-3">
                              <Button
                                color="success"
                                onClick={() =>
                                  push({
                                    job_title_id: "",
                                    no_of_job: "",
                                    is_saudi: null,
                                    employee_experience: 0,
                                  })
                                }
                                className="d-block ms-auto"
                              >
                                {t("Add More")}
                              </Button>
                            </Col>
                          </>
                        )}
                      </FieldArray>

                      <Col lg={12}>
                        <div className="g_end mt-2 gap-3">
                          <button
                            className="main_btn disabled-button fit_content px-4"
                            type="button"
                            onClick={() => navigate(-1)}
                            disabled={isLoading}
                          >
                            {t("Cancel")}
                          </button>
                          {mode === "add" && (
                            <button
                              className="main_btn fit_content px-4"
                              type="button"
                              onClick={() => toggle1()}
                              disabled={!isValid || !dirty}
                            >
                              {t("View Matching Candidates")}
                            </button>
                          )}
                          <button
                            className="main_btn fit_content px-4"
                            disabled={isLoading || !isValid || !dirty}
                          >
                            {mode === "add" ? t("Create") : t("Update")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                    <Modal
                      isOpen={modal1}
                      toggle={toggle1}
                      className="full_screen_modal"
                      backdrop="static"
                    >
                      <ModalHeader toggle={toggle1}>
                        {t("Matched Candidates List")}
                        {totalCount1 ? `(${totalCount1})` : ""}
                      </ModalHeader>
                      <ModalBody className="pt-0">
                        <MatchedCandidates values={values} />
                      </ModalBody>
                    </Modal>
                  </Form>
                )
              }}
            </Formik>
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default CreatePracticalTraining
